// src/lib/v2ThreeSentences.ts
//
// «Tres cosas que han cambiado» (ops.three_sentences). The API states each
// change as data — the metric, its unit, both values, the relative change and
// the previous period — and the sentence is written here in the reader's
// language. An absent value means the periods cannot be compared: the page
// then says nothing (no «no ha cambiado»).
import type { TranslateFunction } from 'react-admin';
import { formatCount, formatSeconds, formatSignedDelta } from './analyticsFormat';
import { formatV2Share } from './analyticsV2Format';
import type { MetricResultDto, ThreeSentencesItem } from '@/types/analyticsV2';

export type ThreeSentencesView = { kind: 'hidden' } | { kind: 'none' } | { kind: 'changes'; sentences: string[] };

const isItem = (value: unknown): value is ThreeSentencesItem =>
  Boolean(value) && typeof value === 'object' && typeof (value as ThreeSentencesItem).metric === 'string';

const formatValue = (item: ThreeSentencesItem, value: number): string => {
  if (item.unit === 'share') return formatV2Share(value);
  if (item.unit === 'seconds') return formatSeconds(value);
  return formatCount(value);
};

/** «14/09» from «2026-09-14». */
const dayMonth = (iso: string): string => {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
};

export const threeSentenceText = (item: ThreeSentencesItem, translate: TranslateFunction): string => {
  const period =
    item.previous_from === item.previous_to
      ? translate('resources.operacion.changes.periodDay', { day: dayMonth(item.previous_from) })
      : translate('resources.operacion.changes.periodRange', { from: dayMonth(item.previous_from), to: dayMonth(item.previous_to) });
  return translate(`resources.operacion.changes.${item.metric.replace('.', '_')}.${item.direction}`, {
    previous: formatValue(item, item.previous),
    current: formatValue(item, item.current),
    change: formatSignedDelta(item.relative_change * 100, 0, ' %').text,
    period,
  });
};

export const buildThreeSentencesView = (metric: MetricResultDto | undefined, translate: TranslateFunction): ThreeSentencesView => {
  if (!metric || metric.absent || !Array.isArray(metric.value)) return { kind: 'hidden' };
  const items = (metric.value as unknown[]).filter(isItem);
  if (items.length === 0) return { kind: 'none' };
  return { kind: 'changes', sentences: items.map((item) => threeSentenceText(item, translate)) };
};
